import { RISK_FACTORS, RISK_LEVELS } from "./constants";

// Maximum achievable weight across all configured factors.
const MAX_WEIGHT = RISK_FACTORS.reduce((sum, factor) => {
  const maxOption = Math.max(...factor.options.map((o) => o.weight));
  return sum + maxOption;
}, 0);

const weightForFactor = (factorKey, value) => {
  const factor = RISK_FACTORS.find((f) => f.key === factorKey);
  if (!factor) return 0;
  const option = factor.options.find((o) => o.value === value);
  return option ? option.weight : 0;
};

/**
 * Computes an inherent-risk recommendation from the configured risk-factor inputs.
 * Returns the recommended level, a 0-100 score and the raw weight.
 */
export const calculateInherentRisk = (inputs = {}) => {
  const providedKeys = RISK_FACTORS.filter((f) => inputs[f.key]).length;
  if (providedKeys === 0) {
    return { level: RISK_LEVELS.UNSCORED, score: 0, totalWeight: 0 };
  }

  const totalWeight = RISK_FACTORS.reduce(
    (sum, factor) => sum + weightForFactor(factor.key, inputs[factor.key]),
    0
  );

  const score = Math.round((totalWeight / MAX_WEIGHT) * 100);

  let level;
  if (score >= 75) level = RISK_LEVELS.CRITICAL;
  else if (score >= 50) level = RISK_LEVELS.HIGH;
  else if (score >= 25) level = RISK_LEVELS.MEDIUM;
  else level = RISK_LEVELS.LOW;

  return { level, score, totalWeight };
};

const LEVEL_TO_INDEX = {
  [RISK_LEVELS.CRITICAL]: 4,
  [RISK_LEVELS.HIGH]: 3,
  [RISK_LEVELS.MEDIUM]: 2,
  [RISK_LEVELS.LOW]: 1,
  [RISK_LEVELS.UNSCORED]: 0,
};

const INDEX_TO_LEVEL = {
  4: RISK_LEVELS.CRITICAL,
  3: RISK_LEVELS.HIGH,
  2: RISK_LEVELS.MEDIUM,
  1: RISK_LEVELS.LOW,
  0: RISK_LEVELS.UNSCORED,
};

/**
 * Derives a recommended residual risk from the inherent risk and how well the
 * vendor performed against the security criteria (pass ratio 0-1). Strong
 * evidence lowers the residual risk relative to inherent.
 */
export const calculateResidualRisk = (inherentLevel, passRatio = 0) => {
  const inherentIndex = LEVEL_TO_INDEX[inherentLevel] ?? 0;
  if (inherentIndex === 0) return RISK_LEVELS.UNSCORED;

  let reduction = 0;
  if (passRatio >= 0.9) reduction = 2;
  else if (passRatio >= 0.7) reduction = 1;
  else if (passRatio < 0.4) reduction = -1;

  const residualIndex = Math.min(4, Math.max(1, inherentIndex - reduction));
  return INDEX_TO_LEVEL[residualIndex];
};

export const compareRiskLevels = (a, b) =>
  (LEVEL_TO_INDEX[b] ?? 0) - (LEVEL_TO_INDEX[a] ?? 0);
